<!--
  Copyright (c) 2025 NoorWays. All rights reserved.
  Licensed under the terms specified in the LICENSE.txt file.
-->

<template>
  <div class="citation-checker-page">
    <!-- SEO-Optimized Hero Section -->
    <header class="hero">
      <div class="container">
        <h1 class="hero-title">{{ $t('citationChecker.h1Title') }}</h1>
        <p class="hero-subtitle">{{ $t('seo.citationChecker.description') }}</p>
        <div class="hero-actions">
          <router-link to="/editor" class="btn btn-primary btn-large">
            {{ $t('citationChecker.tryFree') }}
          </router-link>
          <router-link to="/pricing" class="btn btn-secondary">
            {{ $t('citationChecker.viewPricing') }}
          </router-link>
        </div>
        <div class="trust-indicators">
          <span class="trust-item">✓ {{ $t('citationChecker.trust.accurate') }}</span>
          <span class="trust-item">✓ {{ $t('citationChecker.trust.comprehensive') }}</span>
          <span class="trust-item">✓ {{ $t('citationChecker.trust.instant') }}</span>
        </div>
      </div>
    </header>

    <!-- Features Comparison Section -->
    <section class="features-comparison">
      <div class="container">
        <h2>{{ $t('citationChecker.comparison.title') }}</h2>
        <div class="comparison-table">
          <div class="comparison-header">
            <div class="feature-col">{{ $t('citationChecker.comparison.feature') }}</div>
            <div class="noorwriter-col">n.writer</div>
            <div class="competitor-col">{{ $t('citationChecker.comparison.others') }}</div>
          </div>
          <div class="comparison-row">
            <div class="feature-col">{{ $t('citationChecker.features.verification') }}</div>
            <div class="noorwriter-col">✅</div>
            <div class="competitor-col">❌</div>
          </div>
          <div class="comparison-row">
            <div class="feature-col">{{ $t('citationChecker.features.multiFormat') }}</div>
            <div class="noorwriter-col">✅ {{ $t('citationChecker.allFormats') }}</div>
            <div class="competitor-col">❌ {{ $t('citationChecker.limitedFormats') }}</div>
          </div>
          <div class="comparison-row">
            <div class="feature-col">{{ $t('citationChecker.features.suggestions') }}</div>
            <div class="noorwriter-col">✅</div>
            <div class="competitor-col">⚠️</div>
          </div>
          <div class="comparison-row">
            <div class="feature-col">{{ $t('citationChecker.features.accuracy') }}</div>
            <div class="noorwriter-col">✅</div>
            <div class="competitor-col">⚠️</div>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="how-it-works">
      <div class="container">
        <h2>{{ $t('citationChecker.howItWorks.title') }}</h2>
        <div class="steps-grid">
          <div class="step">
            <div class="step-number">1</div>
            <h3>{{ $t('citationChecker.howItWorks.step1.title') }}</h3>
            <p>{{ $t('citationChecker.howItWorks.step1.description') }}</p>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <h3>{{ $t('citationChecker.howItWorks.step2.title') }}</h3>
            <p>{{ $t('citationChecker.howItWorks.step2.description') }}</p>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <h3>{{ $t('citationChecker.howItWorks.step3.title') }}</h3>
            <p>{{ $t('citationChecker.howItWorks.step3.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Use Cases Section -->
    <section class="use-cases">
      <div class="container">
        <h2>{{ $t('citationChecker.useCases.title') }}</h2>
        <div class="use-cases-grid">
          <div class="use-case-card">
            <h3>{{ $t('citationChecker.useCases.students.title') }}</h3>
            <p>{{ $t('citationChecker.useCases.students.description') }}</p>
          </div>
          <div class="use-case-card">
            <h3>{{ $t('citationChecker.useCases.researchers.title') }}</h3>
            <p>{{ $t('citationChecker.useCases.researchers.description') }}</p>
          </div>
          <div class="use-case-card">
            <h3>{{ $t('citationChecker.useCases.authors.title') }}</h3>
            <p>{{ $t('citationChecker.useCases.authors.description') }}</p>
          </div>
          <div class="use-case-card">
            <h3>{{ $t('citationChecker.useCases.journalists.title') }}</h3>
            <p>{{ $t('citationChecker.useCases.journalists.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits Section -->
    <section class="benefits">
      <div class="container">
        <h2>{{ $t('citationChecker.benefits.title') }}</h2>
        <div class="benefits-grid">
          <div class="benefit-card">
            <h3>{{ $t('citationChecker.benefits.accuracy.title') }}</h3>
            <p>{{ $t('citationChecker.benefits.accuracy.description') }}</p>
          </div>
          <div class="benefit-card">
            <h3>{{ $t('citationChecker.benefits.timeSaving.title') }}</h3>
            <p>{{ $t('citationChecker.benefits.timeSaving.description') }}</p>
          </div>
          <div class="benefit-card">
            <h3>{{ $t('citationChecker.benefits.credibility.title') }}</h3>
            <p>{{ $t('citationChecker.benefits.credibility.description') }}</p>
          </div>
          <div class="benefit-card">
            <h3>{{ $t('citationChecker.benefits.easyToUse.title') }}</h3>
            <p>{{ $t('citationChecker.benefits.easyToUse.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section for Voice Search -->
    <section class="faq">
      <div class="container">
        <h2>{{ $t('citationChecker.faq.title') }}</h2>
        <div class="faq-list">
          <div class="faq-item">
            <h3>{{ $t('citationChecker.faq.q1.question') }}</h3>
            <p>{{ $t('citationChecker.faq.q1.answer') }}</p>
          </div>
          <div class="faq-item">
            <h3>{{ $t('citationChecker.faq.q2.question') }}</h3>
            <p>{{ $t('citationChecker.faq.q2.answer') }}</p>
          </div>
          <div class="faq-item">
            <h3>{{ $t('citationChecker.faq.q3.question') }}</h3>
            <p>{{ $t('citationChecker.faq.q3.answer') }}</p>
          </div>
          <div class="faq-item">
            <h3>{{ $t('citationChecker.faq.q4.question') }}</h3>
            <p>{{ $t('citationChecker.faq.q4.answer') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta">
      <div class="container">
        <h2>{{ $t('citationChecker.cta.title') }}</h2>
        <p>{{ $t('citationChecker.cta.description') }}</p>
        <router-link to="/editor" class="btn btn-primary btn-large">
          {{ $t('citationChecker.cta.button') }}
        </router-link>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// SEO optimization for Citation Checker page
useHead({
  title: () => t('seo.citationChecker.title'),
  meta: [
    {
      name: 'description',
      content: () => t('seo.citationChecker.description'),
    },
    {
      name: 'keywords',
      content: () => t('seo.citationChecker.keywords'),
    },
    {
      name: 'robots',
      content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    },
    {
      name: 'author',
      content: 'n.writer',
    },
    {
      property: 'og:title',
      content: () => t('seo.citationChecker.title'),
    },
    {
      property: 'og:description',
      content: () => t('seo.citationChecker.description'),
    },
    {
      property: 'og:url',
      content: 'https://n-writer.app/citation-checker',
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      property: 'og:site_name',
      content: 'n.writer',
    },
    {
      property: 'og:locale',
      content: 'en_US',
    },
    {
      property: 'og:locale:alternate',
      content: 'fr_FR',
    },
    {
      property: 'og:locale:alternate',
      content: 'es_ES',
    },
    {
      property: 'og:image',
      content: 'https://n-writer.app/images/og-citation-checker.jpg',
    },
    {
      property: 'og:image:width',
      content: '1200',
    },
    {
      property: 'og:image:height',
      content: '630',
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:title',
      content: () => t('seo.citationChecker.title'),
    },
    {
      name: 'twitter:description',
      content: () => t('seo.citationChecker.description'),
    },
    {
      name: 'twitter:image',
      content: 'https://n-writer.app/images/twitter-citation-checker.jpg',
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: 'https://n-writer.app/citation-checker',
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://n-writer.app',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Citation Checker',
            item: 'https://n-writer.app/citation-checker',
          },
        ],
      }),
    },
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: t('seo.citationChecker.title'),
        description: t('seo.citationChecker.description'),
        url: 'https://n-writer.app/citation-checker',
        mainEntity: {
          '@type': 'SoftwareApplication',
          name: 'n.writer Citation Checker',
          applicationCategory: 'WritingApplication',
          operatingSystem: 'Web Browser',
          softwareVersion: '2.0',
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.8',
            ratingCount: '1250',
          },
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
          },
          featureList: [
            'Citation Verification',
            'Quote Checking',
            'Source Validation',
            'Format Support',
            'Accuracy Analysis',
          ],
          screenshot: 'https://n-writer.app/images/citation-checker-screenshot.jpg',
        },
      }),
    },
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: t('citationChecker.faq.q1.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('citationChecker.faq.q1.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('citationChecker.faq.q2.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('citationChecker.faq.q2.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('citationChecker.faq.q3.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('citationChecker.faq.q3.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('citationChecker.faq.q4.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('citationChecker.faq.q4.answer'),
            },
          },
        ],
      }),
    },
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How to Check Citations',
        description: 'Step-by-step guide to using n.writer citation checker',
        step: [
          {
            '@type': 'HowToStep',
            position: 1,
            name: 'Enter Your Text',
            text: 'Paste your document with citations and references that need verification',
          },
          {
            '@type': 'HowToStep',
            position: 2,
            name: 'Smart Analysis',
            text: 'Our AI analyzes citations, verifies sources, and checks format accuracy',
          },
          {
            '@type': 'HowToStep',
            position: 3,
            name: 'Get Recommendations',
            text: 'Receive detailed feedback on citation accuracy and format improvements',
          },
        ],
      }),
    },
  ],
})
</script>

<style lang="scss" scoped>
.citation-checker-page {
  min-height: 100vh;
}

.hero {
  background: linear-gradient(135deg, #1E293B 0%, #0F172A 100%);
  color: white;
  padding: 5rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;

  // Animated orbs
  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.4;
    animation: float 8s ease-in-out infinite;
  }

  &::before {
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, #1E40AF 0%, transparent 70%);
    top: -100px;
    right: -50px;
  }

  &::after {
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, #3B82F6 0%, transparent 70%);
    bottom: -50px;
    left: -50px;
    animation-delay: -3s;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 1;
  }

  &-title {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #FFFFFF 0%, #CBD5E1 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &-subtitle {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    color: #CBD5E1;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
  }

  &-actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: 2rem;
  }
}

@keyframes float {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-20px) scale(1.05); }
}

.trust-indicators {
  display: flex;
  gap: 2rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;

  .trust-item {
    font-size: 1rem;
    color: #3B82F6;
    background: rgba(30, 64, 175, 0.1);
    padding: 0.5rem 1rem;
    border-radius: 2rem;
    border: 1px solid rgba(59, 130, 246, 0.2);
  }
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.875rem 1.75rem;
  border-radius: 0.75rem;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;

  &-primary {
    background: linear-gradient(135deg, #1E40AF 0%, #1E3A8A 100%);
    color: white;
    box-shadow: 0 4px 20px rgba(30, 64, 175, 0.4);

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 30px rgba(30, 64, 175, 0.5);
    }
  }

  &-secondary {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: #3B82F6;
      transform: translateY(-3px);
    }
  }

  &-large {
    padding: 1rem 2rem;
    font-size: 1.125rem;
  }
}

.features-comparison,
.how-it-works,
.use-cases,
.benefits,
.faq,
.cta {
  padding: 5rem 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }

  h2 {
    text-align: center;
    font-size: 2.5rem;
    margin-bottom: 3rem;
    color: var(--nw-brand-slate, #1E293B);
  }
}

.comparison-table {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 0;
  background: white;
  border-radius: 1.5rem;
  overflow: hidden;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
}

.comparison-header {
  display: contents;

  > div {
    background: linear-gradient(135deg, #1E40AF 0%, #1E3A8A 100%);
    color: white;
    padding: 1.25rem;
    font-weight: 600;
  }
}

.comparison-row {
  display: contents;

  > div {
    padding: 1.25rem;
    border-bottom: 1px solid #E5E7EB;
    background: white;
  }

  &:last-child > div {
    border-bottom: none;
  }
}

.feature-col {
  font-weight: 500;
  color: #374151;
}

.noorwriter-col {
  text-align: center;
  color: #1E40AF;
  font-weight: 600;
}

.competitor-col {
  text-align: center;
  color: #6B7280;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.step {
  text-align: center;
  padding: 2.5rem 2rem;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(30, 64, 175, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(30, 64, 175, 0.15);
    border-color: rgba(30, 64, 175, 0.3);
  }

  &-number {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #1E40AF 0%, #1E3A8A 100%);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 auto 1.25rem;
    box-shadow: 0 4px 15px rgba(30, 64, 175, 0.3);
  }

  h3 {
    margin-bottom: 1rem;
    color: #1E293B;
  }

  p {
    color: #64748B;
    line-height: 1.6;
  }
}

.use-cases-grid,
.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 2rem;
}

.use-case-card,
.benefit-card {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(30, 64, 175, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(30, 64, 175, 0.15);
    border-color: rgba(30, 64, 175, 0.3);

    h3 {
      color: #1E3A8A;
    }
  }

  h3 {
    margin-bottom: 1rem;
    color: #1E40AF;
    transition: color 0.3s ease;
  }

  p {
    color: #64748B;
    line-height: 1.6;
  }
}

.faq {
  background: #F8FAFC;
}

.faq-list {
  max-width: 800px;
  margin: 0 auto;
}

.faq-item {
  margin-bottom: 1.5rem;
  padding: 1.5rem 2rem;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

  h3 {
    margin-bottom: 0.75rem;
    color: #1E293B;
    font-size: 1.1rem;
  }

  p {
    color: #64748B;
    line-height: 1.7;
    margin: 0;
  }
}

.cta {
  background: linear-gradient(135deg, #EFF6FF 0%, #CCFBF1 100%);
  text-align: center;

  h2 {
    color: #1E3A8A;
  }

  p {
    color: #172554;
    font-size: 1.125rem;
    margin-bottom: 2rem;
  }
}

@media (max-width: 768px) {
  .hero {
    padding: 3rem 0;

    &-title {
      font-size: 2rem;
    }
  }

  .comparison-table {
    grid-template-columns: 1fr;
    border-radius: 1rem;

    .comparison-header {
      display: none;
    }
  }

  .trust-indicators {
    flex-direction: column;
    gap: 0.75rem;
  }

  .features-comparison,
  .how-it-works,
  .use-cases,
  .benefits,
  .faq,
  .cta {
    padding: 3rem 0;

    h2 {
      font-size: 1.75rem;
    }
  }
}
</style>

